import memoize from 'lodash/memoize';
import moment from 'moment-timezone';
import { NO_TIMEZONE, TIMEZONES } from '@axiom/const';
import type {
  TimeZone,
  TimeZoneIana,
  TimeZoneIanaAll,
  TimeZoneOffset,
} from '@axiom/validation';

type DropdownType = { label: string; value: TimeZone['id'] };

export const CalendarTimeZoneUtil = {
  getDisplayZone: (timezone: TimeZone): DropdownType['label'] => {
    const nowWithOffset = moment(new Date()).tz(timezone.iana).format('h:mm a');
    return `${timezone.abbr} - ${timezone.name} (${nowWithOffset})`;
  },
  isDaylightSavings: (date: Date = new Date()) => {
    return moment(date).isDST();
  },
  getZonesFromOffset: (offset: TimeZoneOffset) => {
    return TIMEZONES.filter((tz: TimeZone) => tz.offset === offset);
  },
  getZonesFromIana: (iana?: TimeZoneIanaAll): TimeZone[] => {
    return TIMEZONES.filter((tz: TimeZone) => tz.iana === iana);
  },

  getZoneFromId: memoize((id?: TimeZone['id']): TimeZone => {
    return (
      (id && TIMEZONES.find((tz: TimeZone) => tz.id === id)) || NO_TIMEZONE
    );
  }),

  getZoneFromMomentZone: (momentZone: moment.Moment) => {
    const zoneName = momentZone.zoneName();
    const zoneIana = moment(momentZone).tz();

    const items = TIMEZONES.filter(
      (tz: TimeZone) => tz.momentName === zoneName
    );

    return items.length === 1
      ? items[0]
      : items.find(item => item.iana === zoneIana);
  },

  getDropDownOptions: memoize((): DropdownType[] => {
    const used = new Set<TimeZoneIana>();

    return TIMEZONES.reduce<DropdownType[]>(
      (data: DropdownType[], zone: TimeZone) => {
        const mZone = moment.tz(new Date(), zone.iana);

        if (!used.has(zone.iana) && zone.momentName === mZone.zoneName()) {
          data.push({
            label: CalendarTimeZoneUtil.getDisplayZone(zone),
            value: zone.id,
          });

          used.add(zone.iana);
        }

        return data;
      },
      []
    );
  }),
  getTimeZone: (talentIana?: TimeZoneIanaAll) => {
    let foundZone: TimeZone;

    /**
     * If talentIana is not a valid IANA in moment.tz.names()
     * moment will throw an error.
     * Handling it gracefully here with this try/catch
     * */
    try {
      const zones = CalendarTimeZoneUtil.getZonesFromIana(talentIana);

      if (zones.length === 1) {
        [foundZone] = zones as [TimeZone];
      } else if (zones.length > 1) {
        const mName = moment
          .tz(new Date(), talentIana || moment.tz.guess())
          .zoneName();
        foundZone = zones.find(z => z.momentName === mName) as TimeZone;
      } else {
        foundZone =
          CalendarTimeZoneUtil.getZoneFromMomentZone(
            moment.tz(new Date(), talentIana || moment.tz.guess())
          ) || NO_TIMEZONE;
      }
    } catch {
      return NO_TIMEZONE;
    }
    return foundZone;
  },
};
